import { Button, Col, Form, Row, Select, Spin, message } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { OptionProps } from "antd/es/select";
import { ApiService } from "../../api";
import { debounce } from "lodash";
import { openNotificationWithIcon } from "../../constants";

type Props = {
  schema: any;
  formProps: any;
  formId?: number;
  requestId?: number;
  readonly?: boolean;
  values?: any;
  onSuccess: (result: any) => void;
  duplicateRequestId?: number | undefined;
  setDuplicateRequestId?: (duplicateRequestId: number | undefined) => void;
};

const formItemLayout = {
  labelCol: {
    xs: { span: 8 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 18 },
    sm: { span: 18 },
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 18, offset: 8 },
    sm: { span: 18, offset: 8 },
  },
};

const submitButtonLayout = {
  wrapperCol: {
    xs: { span: 4, offset: 20 },
    sm: { span: 4, offset: 20 },
  },
};

const LinkedObjectFormComponent = ({
  schema,
  formProps,
  formId,
  requestId,
  readonly,
  values,
  onSuccess,
  duplicateRequestId,
  setDuplicateRequestId,
}: Props) => {
  const [form] = Form.useForm();
  const [requests, setRequests] = useState<OptionProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initializing, setInitializing] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (duplicateRequestId === undefined) {
        setLoading(true);

        getFormValues(requestId, formId);

        setLoading(false);
      }
    })();
  }, [requestId, formId]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      getFormValues(duplicateRequestId, formId);

      setLoading(false);
    })();
  }, [duplicateRequestId]);

  const getFormValues = async (requestId?: number, form_id?: number) => {
    if (!requestId) return undefined;
    const result = await ApiService.apiMainRequestFormRetrieve(
      form_id!,
      requestId!
    );

    const requestIds = result.map((x: any) => x.id);

    const requests = result.map((x: any) => {
      return { value: x.id, label: x.request_number, children: null };
    });

    setRequests(requests);
    form.setFieldsValue({ requests: requestIds });
  };

  const handleRequestSearch = async (value: string) => {
    const result = (await ApiService.apiMainGetRequestsByNumberList(value)).map(
      (x) => ({
        value: x.id.toString(),
        label: x.number,
        children: null,
      })
    );
    setRequests(result);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const result = await ApiService.apiMainRequestFormCreate({
        form_id: formId ?? 0,
        request_id: requestId ?? null,
        data: values,
      });
      openNotificationWithIcon("success", "Form successfully saved");
      onSuccess(result);
      getFormValues(requestId, formId);
    } catch (e) {
      openNotificationWithIcon("error");
      console.log(e);
    }
    setLoading(false);
  };
  return (
    <>
      <Spin spinning={initializing}>
        <Form
          form={form}
          onFinish={onFinish}
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.List name="requests">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    {...formItemLayout}
                    label={"Request"}
                    required={false}
                    key={field.key}
                  >
                    <Form.Item
                      {...field}
                      validateTrigger={["onChange", "onBlur"]}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message:
                            "Please input request's number or delete this field.",
                        },
                      ]}
                      noStyle
                    >
                      <Select
                        showSearch
                        disabled={loading}
                        optionLabelProp="label"
                        filterOption={false}
                        style={{ width: "60%" }}
                        onSearch={debounce(handleRequestSearch, 300)}
                        options={requests}
                        notFoundContent={loading ? <Spin size="small" /> : null}
                        placeholder="Type request number.."
                      ></Select>
                    </Form.Item>
                    {fields.length > 1 ? (
                      <MinusCircleOutlined
                        style={{ marginLeft: 10 }}
                        className="dynamic-delete-button"
                        disabled={loading}
                        onClick={() => remove(field.name)}
                      />
                    ) : null}
                  </Form.Item>
                ))}
                <Form.Item {...formItemLayoutWithOutLabel}>
                  <Button
                    type="dashed"
                    style={{ width: "60%" }}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    disabled={loading}
                  >
                    Add field
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
          <Row justify="end">
            <Col>
              <Button
                style={{ width: 100, marginTop: "1em" }}
                type="primary"
                loading={loading}
                htmlType="submit"
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </>
  );
};

export default LinkedObjectFormComponent;
